import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  User,
  BarChart3,
  Target,
  UserCheck,
  Shield,
  FileArchive,
  Mail,
  ChevronDown,
  ChevronRight,
  Bot,
  Users2,
  X,
  Building2,
  Calendar,
  PanelLeftClose,
  PanelLeftOpen,
  BookOpen,
  ExternalLink
} from 'lucide-react'
import { cn } from 'lib/utils'
import { useAuth } from 'contexts/AuthContext'
import { useTheme } from 'contexts/ThemeContext'
import { Button } from 'components/ui/Button'
import { SettingsCogIcon } from 'components/icons/SettingsCogIcon'
import { organizationsApi, rolesApi, type Organization, type UserRole } from 'lib/api'

interface SidebarProps {
  isOpen?: boolean
  onClose?: () => void
  canCollapseOnDesktop?: boolean
  showCollapseToggle?: boolean
  isDesktopCollapsed?: boolean
  onCollapseDesktop?: () => void
  onExpandDesktop?: () => void
}

interface NavigationSection {
  title: string
  items: NavigationItem[]
}

interface NavigationItem {
  name: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  adminOnly?: boolean
  external?: boolean
}

const USER_GUIDE_URL = process.env.REACT_APP_GUIDE_URL?.trim() || 'https://selektia.io/guide/index.html'

function DashboardGaugeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.1202 11.5509L14.3767 15.1168C14.4496 15.6409 14.3621 16.1756 14.1268 16.6451C13.8914 17.1146 13.5201 17.495 13.0655 17.7324C12.6109 17.9698 12.096 18.0522 11.5939 17.9678C11.0918 17.8834 10.628 17.6366 10.2681 17.2624C9.90831 16.8881 9.67075 16.4054 9.58915 15.8827C9.50755 15.36 9.58605 14.8238 9.81352 14.3501C10.041 13.8765 10.4059 13.4894 10.8565 13.2438C11.3071 12.9982 11.8205 12.9065 12.3239 12.9817L15.7484 10.1226C15.9361 9.96757 16.172 9.88984 16.4112 9.90417C16.6504 9.9185 16.8761 10.0239 17.0455 10.2003C17.2149 10.3767 17.3161 10.6117 17.3299 10.8608C17.3436 11.1098 17.269 11.3554 17.1202 11.5509ZM23 15.4541C23 12.4163 21.8411 9.50287 19.7782 7.35482C17.7153 5.20676 14.9174 4 12 4C9.08262 4 6.28473 5.20676 4.22183 7.35482C2.15893 9.50287 1 12.4163 1 15.4541C1 15.7329 1.10638 16.0004 1.29575 16.1975C1.48511 16.3947 1.74194 16.5055 2.00974 16.5055H4.48885C4.75097 16.4923 4.99816 16.3746 5.17918 16.1768C5.36021 15.9789 5.46119 15.7162 5.46119 15.4429C5.46119 15.1697 5.36021 14.9069 5.17918 14.709C4.99816 14.5112 4.75097 14.3935 4.48885 14.3803H3.25525C3.44415 12.6916 4.08258 11.0915 5.09851 9.76051L5.96774 10.6656C6.16203 10.8494 6.41679 10.9483 6.67919 10.9417C6.94159 10.9352 7.19151 10.8237 7.37711 10.6304C7.56271 10.4372 7.66976 10.1769 7.67605 9.9037C7.68234 9.63046 7.5874 9.36519 7.41091 9.16288L6.54168 8.26769C7.81993 7.20994 9.3566 6.54516 10.9784 6.34834V7.63286C10.9717 7.77672 10.9931 7.9205 11.0414 8.05547C11.0897 8.19044 11.1638 8.3138 11.2592 8.41807C11.3546 8.52234 11.4693 8.60535 11.5965 8.66207C11.7236 8.71879 11.8605 8.74803 11.9988 8.74803C12.1371 8.74803 12.274 8.71879 12.4011 8.66207C12.5283 8.60535 12.643 8.52234 12.7385 8.41807C12.8339 8.3138 12.908 8.19044 12.9562 8.05547C13.0045 7.9205 13.0259 7.77672 13.0193 7.63286V6.35082C14.988 6.59109 16.8214 7.51554 18.2232 8.97479C19.625 10.434 20.5134 12.3428 20.7447 14.3927H19.5112C19.249 14.4059 19.0018 14.5236 18.8208 14.7214C18.6398 14.9193 18.5388 15.182 18.5388 15.4553C18.5388 15.7286 18.6398 15.9913 18.8208 16.1892C19.0018 16.387 19.249 16.5047 19.5112 16.5179H21.976C22.2471 16.5179 22.5073 16.4059 22.6992 16.2065C22.8912 16.007 22.9994 15.7364 23 15.4541Z" fill="currentColor" />
    </svg>
  )
}

function CandidatesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.2195 7.21886C16.2195 9.54903 14.3308 11.4377 12.0006 11.4377C9.67043 11.4377 7.78174 9.54903 7.78174 7.21886C7.78174 4.88869 9.67043 3 12.0006 3C14.3308 3 16.2195 4.88869 16.2195 7.21886Z" fill="currentColor" />
      <path d="M4.65432 21.0001H19.3464C19.9497 21.0001 20.4381 20.5117 20.4381 19.9084C20.4381 16.1631 17.4008 13.1248 13.6544 13.1248H10.3456C6.60024 13.1248 3.56189 16.1621 3.56189 19.9084C3.56189 20.5117 4.05031 21.0001 4.65355 21.0001H4.65432Z" fill="currentColor" />
    </svg>
  )
}

function OffersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M20 6C21.1377 6 22 6.86419 22 8V19C22 20.1358 21.1377 21 20 21H4C2.86227 21 2 20.1358 2 19V8C2 6.86419 2.86227 6 4 6H8C8 4.47473 9.47217 3 11 3H13C14.4923 3 15.9316 4.40703 15.9976 5.89383L16.0177 6H20ZM15.189 8C14.7083 8 14.2956 7.65801 14.2064 7.18569L14.0174 6.18569L14 6C14 5.57853 13.4225 5 13 5H11C10.5775 5 10 5.57853 10 6V7C10 7.55228 9.55228 8 9 8H4V19H20V8H15.189ZM12 11C10.8954 11 10 11.8954 10 13C10 14.1046 10.8954 15 12 15C13.1046 15 14 14.1046 14 13C14 11.8954 13.1046 11 12 11Z" fill="currentColor" />
      <path d="M8 8V6H16V8H8Z" fill="currentColor" />
      <path d="M4 14V12H20V14H4Z" fill="currentColor" />
    </svg>
  )
}

function EmailTemplatesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 4C20.6569 4 22 5.34315 22 7V17C22 18.6569 20.6569 20 19 20H5C3.34315 20 2 18.6569 2 17V7C2 5.34315 3.34315 4 5 4H19ZM5 6C4.44772 6 4 6.44772 4 7V17C4 17.5523 4.44772 18 5 18H19C19.5523 18 20 17.5523 20 17V7C20 6.44772 19.5523 6 19 6H5ZM17.4121 7.71387C17.8836 7.42624 18.4995 7.57541 18.7871 8.04688C19.0747 8.51834 18.9256 9.13424 18.4541 9.42188L12.5205 13.041C12.1992 13.237 11.7949 13.2367 11.4746 13.0391L5.61035 9.41895C5.14045 9.12886 4.99515 8.51291 5.28516 8.04297C5.57525 7.573 6.19117 7.42671 6.66113 7.7168L12.0029 11.0146L17.4121 7.71387Z" fill="currentColor" />
    </svg>
  )
}

function ActiveArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14.5 15.0859C14.5 15.9768 15.5771 16.423 16.2071 15.793L18.7929 13.2072C19.1834 12.8167 19.1834 12.1835 18.7929 11.793L16.2071 9.2072C15.5771 8.57724 14.5 9.02341 14.5 9.91431L14.5 15.0859Z" fill="white" />
      <path fillRule="evenodd" clipRule="evenodd" d="M4 12.5C4 12.2239 4.22386 12 4.5 12H14.5V13H4.5C4.22386 13 4 12.7761 4 12.5Z" fill="white" />
      <path fillRule="evenodd" clipRule="evenodd" d="M3.5 12.5C3.5 11.9477 3.94772 11.5 4.5 11.5H15V13.5H4.5C3.94772 13.5 3.5 13.0523 3.5 12.5Z" fill="white" />
    </svg>
  )
}

export function Sidebar({
  isOpen = false,
  onClose,
  canCollapseOnDesktop = false,
  showCollapseToggle = false,
  isDesktopCollapsed = false,
  onCollapseDesktop,
  onExpandDesktop,
}: SidebarProps) {
  const { t } = useTranslation()
  const { user, hasPermission } = useAuth()
  const { theme } = useTheme()
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [userRoles, setUserRoles] = useState<UserRole[]>([])
  const logoSrc = theme === 'dark' ? '/logodark512.png' : '/logo512.png'
  const railCollapsed = canCollapseOnDesktop || isDesktopCollapsed
  const [contentCollapsed, setContentCollapsed] = useState(railCollapsed)

  useEffect(() => {
    if (canCollapseOnDesktop) {
      setContentCollapsed(true)
      return
    }
    if (!isDesktopCollapsed) {
      setContentCollapsed(false)
      return
    }
    const timeoutId = window.setTimeout(() => setContentCollapsed(true), 280)
    return () => window.clearTimeout(timeoutId)
  }, [canCollapseOnDesktop, isDesktopCollapsed])
  
  // Check if user is an organization admin (has organization and is_staff but not superuser)
  const isOrgAdmin = user?.is_staff && !user?.is_superuser && user?.organization
  // Check if user is super admin (main admin email or superuser, but NOT organization admin)
  const isAdmin = (user?.email === 'admin@selektia.ma' || user?.is_superuser) && !isOrgAdmin
  
  // Fetch organization if user has organization ID but not full object
  useEffect(() => {
    const fetchOrganization = async () => {
      const userOrg = user?.organization
      if (userOrg) {
        // If organization is already an object, use it directly
        if (typeof userOrg === 'object' && userOrg !== null && 'name' in userOrg) {
          setOrganization(userOrg as Organization)
        } 
        // If organization is just an ID, fetch it
        else if (typeof userOrg === 'number') {
          try {
            const response = await organizationsApi.getById(userOrg)
            setOrganization(response.data)
          } catch (error) {
            console.error('Error fetching organization:', error)
          }
        }
      } else {
        setOrganization(null)
      }
    }
    
    if (user) {
      fetchOrganization()
    }
  }, [user])

  // Fetch user roles (only organization roles)
  useEffect(() => {
    const fetchUserRoles = async () => {
      if (user?.id && organization) {
        try {
          console.log('Fetching user roles for user:', user.id, 'organization:', organization.id)
          // Call with user_id to get specific user's roles
          const response = await rolesApi.getUserRoles(user.id)
          console.log('User roles response:', response)
          console.log('User roles data:', response.data)
          
          const allRoles = Array.isArray(response.data) ? response.data : []
          console.log('All roles array:', allRoles)
          console.log('All roles count:', allRoles.length)
          
          if (allRoles.length === 0) {
            console.warn('⚠️ No roles found for user. Please check:')
            console.warn('1. Does the user have roles assigned?')
            console.warn('2. Are the roles linked to the organization?')
            console.warn('3. Run: python manage.py fix_user_role_organizations')
          }
          
          // Filter to show only organization roles (role_type === 'organization')
          const orgRoles = allRoles.filter((userRole: UserRole) => {
            const isOrgRole = userRole.role_type === 'organization'
            console.log('Role:', userRole.role_name, 'type:', userRole.role_type, 'isOrgRole:', isOrgRole, 'organization:', userRole.organization)
            return isOrgRole
          })
          console.log('✅ Filtered organization roles:', orgRoles)
          console.log('✅ Organization roles count:', orgRoles.length)
          setUserRoles(orgRoles)
        } catch (error: any) {
          console.error('❌ Error fetching user roles:', error)
          console.error('Error details:', error.response?.data || error.message)
          if (error.response?.status === 403) {
            console.error('Permission denied. User might not have view_roles permission.')
          }
          setUserRoles([])
        }
      } else {
        console.log('Not fetching roles - user:', user?.id, 'organization:', organization?.id)
        setUserRoles([])
      }
    }
    
    if (user && organization) {
      fetchUserRoles()
    } else {
      setUserRoles([])
    }
  }, [user, organization])
  
  // Debug: Log admin status (remove in production)
  useEffect(() => {
    if (user) {
      console.log('Sidebar Debug:', {
        userEmail: user?.email,
        is_staff: user?.is_staff,
        is_superuser: user?.is_superuser,
        isAdmin,
        isOrgAdmin,
        organization: user?.organization,
        organizationsTranslation: t('nav.organizations'),
        userObject: user
      })
    }
  }, [user, isAdmin, isOrgAdmin, t])
  
  const navigationSections: NavigationSection[] = [
    {
      title: '',
      items: [
        { name: t('nav.dashboard'), href: '/dashboard', icon: DashboardGaugeIcon },
        { name: t('nav.offers'), href: '/offers', icon: OffersIcon },
        { name: t('nav.candidates'), href: '/candidates', icon: CandidatesIcon },
        { name: `${t('nav.aiAssistant')} (Beta)`, href: '/copilot', icon: Bot },
        { name: t('nav.emailTemplates'), href: '/admin/email-templates', icon: EmailTemplatesIcon },
        { name: t('nav.hiringProcess'), href: '/hiring-process', icon: UserCheck },
        { name: t('nav.settings'), href: '/settings', icon: SettingsCogIcon },
      ]
    },
    {
      title: t('nav.administration'),
      items: [
        { name: t('nav.organizations'), href: '/organizations', icon: Building2, adminOnly: true },
        { name: t('nav.users'), href: '/admin/users', icon: Users },
        { name: t('nav.roles'), href: '/admin/roles', icon: UserCheck },
      ]
    }
  ]

  useEffect(() => {
    if (!isOpen) {
      document.body.style.removeProperty('overflow')
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose?.()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.setProperty('overflow', 'hidden')

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.removeProperty('overflow')
    }
  }, [isOpen, onClose])

  const renderNavigationItem = (item: NavigationItem, collapsed: boolean) => item.external ? (
    <a
      key={item.name}
      href={item.href}
      target="_blank"
      rel="noopener noreferrer"
      title={item.name}
      onClick={() => onClose?.()}
      className={cn(
        'group flex h-14 w-full items-center text-sm font-medium text-foreground transition-colors hover:bg-[#19A6A6]/50',
        collapsed ? 'justify-center px-0' : 'justify-between px-4 pb-4 pl-8 pr-4 pt-4'
      )}
    >
      <span className={cn('flex items-center', collapsed ? '' : 'min-w-0 flex-1 gap-2.5')}>
        <item.icon className="h-6 w-6 shrink-0" />
        {collapsed ? (
          <span className="sr-only">{item.name}</span>
        ) : (
          <span className="h-6 min-w-0 flex-1 truncate text-[13px] leading-6">{item.name}</span>
        )}
      </span>
      {!collapsed && (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center">
          <ExternalLink className="h-4 w-4 text-muted-foreground" />
        </span>
      )}
    </a>
  ) : (
    <NavLink
      key={item.name}
      to={item.href}
      title={item.name}
      onClick={() => onClose?.()}
      className={({ isActive }) =>
        cn(
          'group flex h-14 w-full items-center text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-0' : 'justify-between px-4 pb-4 pl-8 pr-4 pt-4',
          isActive ? 'bg-[#19A6A6] text-white' : 'text-foreground hover:bg-[#19A6A6]/50'
        )
      }
    >
      {({ isActive }) => (
        <>
          <span className={cn('flex items-center', collapsed ? '' : 'min-w-0 flex-1 gap-2.5')}>
            <item.icon className="h-6 w-6 shrink-0" />
            {collapsed ? (
              <span className="sr-only">{item.name}</span>
            ) : (
              <span className="h-6 min-w-0 flex-1 truncate text-[13px] leading-6">{item.name}</span>
            )}
          </span>
          {!collapsed && (isActive ? <ActiveArrowIcon className="h-6 w-6 shrink-0" /> : <span className="h-6 w-6 shrink-0" />)}
        </>
      )}
    </NavLink>
  )

  const hasItemAccess = (href: string): boolean => {
    // Map routes to permission codenames
    const routePerm: Record<string, string | undefined> = {
      '/offers': 'view_offers', 
      '/teams': 'manage_teams',
      '/candidates': 'view_candidates',
      '/copilot': 'use_ai_assistant',
      // '/analytics': 'view_analytics',
      '/matchings': 'use_ai_matching',
      '/hiring-process': 'view_hiring_process',
      '/admin/roles': 'view_roles',
      '/admin/users': 'view_users',
      '/organizations': undefined, // Only visible to admins via adminOnly flag
      // dashboard and settings are allowed for authenticated users
    }
    const required = routePerm[href]
    
    // Organization admins should have access to core features (offers, candidates, users)
    // even if permissions aren't loaded yet, as they manage their organization's data
    if (isOrgAdmin && ['/offers', '/candidates', '/copilot', '/matchings', '/admin/users'].includes(href)) {
      return true
    }
    
    return !required || hasPermission(required)
  }

  const renderNavigationSection = (section: NavigationSection, collapsed: boolean) => {
    // Filter by admin and permission
    const visibleItems = section.items.filter(item => {
      // For organization admins, show core items (offers, candidates, users) even without explicit adminOnly check
      const adminCheck = !item.adminOnly || isAdmin || (isOrgAdmin && ['/offers', '/candidates', '/admin/users'].includes(item.href))
      const accessCheck = hasItemAccess(item.href)
      // Debug organizations item
      if (item.href === '/organizations') {
        console.log('Organizations menu item check:', {
          adminOnly: item.adminOnly,
          isAdmin,
          isOrgAdmin,
          adminCheck,
          accessCheck,
          visible: adminCheck && accessCheck
        })
      }
      return adminCheck && accessCheck
    })
    
    if (visibleItems.length === 0) return null

    return (
      <div key={section.title} className={collapsed ? 'mb-3' : 'mb-8'}>
        {section.title && (
          collapsed ? (
            <div className="mx-auto mb-2 mt-2 h-px w-8 bg-border" aria-hidden="true" />
          ) : (
            <div className="h-[42px] px-4 pb-2 pl-8 pt-4">
              <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                {section.title}
              </h3>
            </div>
          )
        )}
        <nav className="space-y-0">
          {visibleItems.map((item) => renderNavigationItem(item, collapsed))}
        </nav>
      </div>
    )
  }

  const renderCollapseToggle = (collapsed: boolean) => (
    <Button
      variant="ghost"
      size="icon"
      className={cn('hidden h-9 w-9 lg:inline-flex', collapsed && 'mx-auto')}
      onClick={() => (collapsed ? onExpandDesktop?.() : onCollapseDesktop?.())}
      aria-label={
        collapsed
          ? t('header.showSidebar', 'Show navigation')
          : t('header.hideSidebar', 'Hide navigation')
      }
      title={
        collapsed
          ? t('header.showSidebar', 'Show navigation')
          : t('header.hideSidebar', 'Hide navigation')
      }
    >
      {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
    </Button>
  )

  const renderSidebarContent = (collapsed: boolean) => (
    <div className={cn('flex h-full w-full flex-col bg-card text-card-foreground', collapsed ? '' : 'w-[264px]')}>
      {collapsed ? (
        <div className="flex w-full flex-col items-center justify-center gap-3 px-0 py-3">
          {showCollapseToggle && renderCollapseToggle(true)}
          <img src="/favicon_48px_bg.png" alt="Selektia" className="mx-auto block h-11 w-11 object-contain object-center" />
        </div>
      ) : (
        <div className="relative flex items-center justify-center">
          <div className="flex h-[132px] w-full items-center justify-center py-4">
            <img src={logoSrc} alt="Selektia" className="h-full w-4/5 object-contain" />
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-3 lg:hidden"
            onClick={() => onClose?.()}
            aria-label="Close sidebar"
          >
            <X className="h-4 w-4" />
          </Button>
          {showCollapseToggle && (
            <div className="absolute right-2 top-3">
              {renderCollapseToggle(false)}
            </div>
          )}
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-0 py-0 sidebar-scroll">
        {!collapsed && (
          <div className="flex h-[42px] items-center justify-center px-4 pb-2 pt-4">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              {t('common.recruitmentManagement')}
            </h3>
          </div>
        )}
        {navigationSections.map((section) => renderNavigationSection(section, collapsed))}
      </div>

      <div className="border-t">
        {renderNavigationItem(
          { name: t('nav.userGuide'), href: USER_GUIDE_URL, icon: BookOpen, external: true },
          collapsed
        )}
      </div>

      <div className={cn('border-t', collapsed ? 'p-2' : 'p-4')}>
        {(() => {
          // If system account (no organization), return empty
          if (!organization) {
            return null
          }
          
          const orgName = organization.name || ''
          const orgLogo = organization.logo
          
          // Get logo URL helper
          const getLogoUrl = (logo?: string): string | null => {
            if (!logo) return null
            if (logo.startsWith('http://') || logo.startsWith('https://')) return logo
            // Media files are served from /media/, not /api/media/
            const envUrl = process.env.REACT_APP_API_URL
            const baseUrl = envUrl && envUrl.trim().length > 0 
              ? envUrl.trim().replace(/\/+$/, '')
              : `http://${window.location.hostname}:8000`
            const logoPath = logo.startsWith('/') ? logo : `/${logo}`
            return `${baseUrl}/media${logoPath}`
          }
          
          return (
            <>
              <div className={cn('flex items-center', collapsed ? 'justify-center' : 'mb-3 space-x-3')}>
                {getLogoUrl(orgLogo) ? (
                  <img
                    src={getLogoUrl(orgLogo)!}
                    alt={orgName}
                    title={orgName}
                    className="h-8 w-8 rounded object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                    }}
                  />
                ) : (
                  <div
                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-[#19A6A6]"
                    title={orgName}
                  >
                    <Building2 className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
                {!collapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {orgName}
                    </p>
                    {user?.full_name && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                        {user.full_name}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )
        })()}
      </div>
    </div>
  )

  const overlayExpanded = canCollapseOnDesktop && !isDesktopCollapsed

  return (
    <>
      <div className={cn(
        'hidden h-full shrink-0 flex-col items-stretch overflow-hidden transition-[width] duration-300 ease-in-out lg:flex',
        railCollapsed ? 'w-16' : 'w-[264px]',
      )}>
        {renderSidebarContent(canCollapseOnDesktop ? true : contentCollapsed)}
      </div>

      {canCollapseOnDesktop && (
        <>
          <div
            className={cn(
              'fixed inset-0 z-[55] hidden bg-black/40 backdrop-blur-sm transition-opacity duration-300 ease-out lg:block',
              overlayExpanded ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
            onClick={() => onCollapseDesktop?.()}
            aria-hidden={!overlayExpanded}
          />
          <div
            className={cn(
              'fixed inset-y-0 left-0 z-[60] hidden w-[264px] shadow-xl transition-transform duration-300 ease-in-out lg:block',
              overlayExpanded ? 'translate-x-0' : 'pointer-events-none -translate-x-full'
            )}
            aria-hidden={!overlayExpanded}
            inert={!overlayExpanded}
          >
            {renderSidebarContent(false)}
          </div>
        </>
      )}

      <div
        className={cn(
          'fixed inset-y-0 left-0 z-[60] w-[264px] shrink-0 transform bg-card shadow-xl transition-transform duration-200 ease-in-out lg:hidden',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {renderSidebarContent(false)}
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
    </>
  )
}
